import { t } from '@avalara/bat-apps-shared/i18n';
import { SCol, SRow } from '@avalara/skylab-sdk/react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory, useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';

import type { OffRampType, PrequalQuestionnaire, QuestionType } from '../../../../../types/prequal-questionnaire';

import conf from '../../../conf';
import { selectState } from '../../../selectors';
import { selectAnswer } from '../../../store/prequal-questionnaire';
import { getCtaUrlWithCampaignId } from '../../helpers/prequal-questionnaire';
import OffRampModal from '../offramp-modal';
import OffRampPage from '../offramp-page';
import Questionnaire from './questionnaire';

const QuestionCol = styled(SCol)`
  width: 765px;
`;

const PrequalQuestionPage = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { questionNumberParam } = useParams<{
    questionNumberParam: string;
  }>();
  const currentQuestionIndex = Number.parseInt(questionNumberParam, 10);

  const { questionnaire } = useSelector(selectState);
  const config = questionnaire.config as PrequalQuestionnaire;
  const { selectedAnswers } = questionnaire;

  if (!config) {
    // TODO: add alert and redirect to `question/start` on page refresh
    // since we will be in the middle of a flow and lose question answer data
    console.log('NO CONFIG FOUND.... trouble approaches');
  }

  const { questions, campaignId } = config;

  const [offRampModal, setOffRampModal] = useState<OffRampType | null>(null);
  const [offRampPage, setOffRampPage] = useState<OffRampType | null>(null);

  const currentQuestion = questions[currentQuestionIndex];

  if (!currentQuestion) {
    // at the end of the flow
    return (
      <Redirect
        to={{
          pathname: '/question/end',
          search: location.search,
        }}
      />
    );
  }

  const { options, otherOptions } = currentQuestion.inputData.inputAnswers;
  const allOptionValues = options.concat(otherOptions || []).map((option) => option.value);
  const pageAnswers = selectedAnswers[currentQuestion.id] || [];

  const activeFollowUps = currentQuestion.followUpQuestions?.filter((q) => pageAnswers.includes(q.answer));
  const anyNonOptionAnswers = pageAnswers.some((answer) => !allOptionValues.includes(answer));

  const isConnectorSelectorEmpty =
    currentQuestion.id === 'integrations' &&
    pageAnswers.includes('My business uses a system not listed above') &&
    !anyNonOptionAnswers;

  const goToNextQuestion = () => {
    history.push({
      pathname: `/question/${currentQuestionIndex + 1}`,
      search: location.search,
    });
  };

  const handleSkip = (question: QuestionType) => {
    dispatch(
      selectAnswer({
        questionId: question.id,
        questionType: question.inputData.inputType,
        answer: 'skipped',
      }),
    );
    goToNextQuestion();
  };

  const getOffRamp = (): undefined | OffRampType => {
    const offRampWithMatchingAnswer = currentQuestion.offRamps?.find(
      (questionOffRamp) =>
        selectedAnswers[currentQuestion.id]?.includes(questionOffRamp.answer) ||
        activeFollowUps?.some((q) => selectedAnswers[q.id]?.includes(questionOffRamp.answer)),
    );

    if (!offRampWithMatchingAnswer) return undefined;
    return config.offRamps?.find((offRamp) => offRamp.id === offRampWithMatchingAnswer.offRampId);
  };

  const handleNextClick = () => {
    const offRampToUse = getOffRamp();
    if (offRampToUse) {
      if (offRampToUse.inputType === 'Modal') {
        setOffRampModal(offRampToUse);
      } else {
        setOffRampPage(offRampToUse);
        history.push({
          pathname: `/question/${currentQuestionIndex}/${offRampToUse.id}`,
          search: location.search,
        });
      }
      return;
    }
    goToNextQuestion();
  };

  if (offRampPage && location.pathname === `/question/${currentQuestionIndex}/${offRampPage.id}`) {
    return <OffRampPage pageData={offRampPage} />;
  }

  const isRegistrationsQuestionnaire = config.name === 'registrations';
  const hasSelectedStates =
    (selectedAnswers.exceedingNexusThresholds?.length || 0) +
      (selectedAnswers.approachingNexusThresholds?.length || 0) >
    0;
  const onLastQuestion = currentQuestionIndex + 1 === questions.length;
  const noStatesSelected = !hasSelectedStates && onLastQuestion;

  const nextButtonDisabled = isRegistrationsQuestionnaire
    ? noStatesSelected
    : !pageAnswers.length ||
      !!activeFollowUps?.some((question) => !selectedAnswers[question.id]?.length) ||
      isConnectorSelectorEmpty;

  const freeTrialUrl = getCtaUrlWithCampaignId(campaignId, `${conf.BUY_CLIENT_URL}/small-business-trial`);

  return (
    <SRow className='flex flex-dir-col align-items-center'>
      {offRampModal && <OffRampModal modalData={offRampModal} setOffRampModal={setOffRampModal} />}

      <QuestionCol>
        <Questionnaire questionSelectedAnswers={pageAnswers} pageQuestionData={currentQuestion} />

        {activeFollowUps?.map((followUp) => (
          <Questionnaire
            key={followUp.id}
            questionSelectedAnswers={selectedAnswers[followUp.id] || []}
            pageQuestionData={followUp}
            isFollowUp={true}
          />
        ))}

        <SRow className='justify-content-space pad-right-sm pad-left-sm margin-bottom-sm'>
          <div>
            <button
              type='button'
              data-ref='buy.prequal.questions.back-button'
              className='secondary active margin-right-sm'
              onClick={() => history.goBack()}
            >
              {t('client.buttons.back')}
            </button>
            <button
              type='button'
              data-ref='buy.prequal.questions.next-button'
              className='primary margin-right-sm'
              disabled={nextButtonDisabled}
              onClick={handleNextClick}
            >
              {t('client.buttons.next')}
            </button>
            {currentQuestion.allowSkip && (
              <button
                type='button'
                data-ref='buy.prequal.questions.skip-button'
                onClick={() => handleSkip(currentQuestion)}
                className='link text-underline'
              >
                {t('client.buttons.skip')}
              </button>
            )}
          </div>
          {currentQuestion.showFreeTrialLink && (
            <div data-ref='buy.prequal.questions.free-trial-link' className='flex align-items-center'>
              <p className='text-center'>
                {t('client.general.ready-to-start-your-free-trial')}
                {'  '}
                <a className='text-underline' href={freeTrialUrl}>
                  {' '}
                  {t('client.general.sign-up-now')}
                </a>
              </p>
            </div>
          )}
        </SRow>
        <hr className='margin-top-lg margin-bottom-lg margin-left-sm margin-right-sm' />
        <SRow className='justify-content-center'>
          <div data-ref='buy.prequal.questions.status' className='text-xs'>
            {t('client.general.question')}
            {currentQuestionIndex + 1} of {questions.length}
          </div>
        </SRow>
      </QuestionCol>
    </SRow>
  );
};

export default PrequalQuestionPage;
